import { useDeferredValue, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Eye, Pencil, UserPlus, Users } from 'lucide-react';
import {
  CustomerStatus,
  PERMISSIONS,
  type CustomerStatus as CustomerStatusType,
  type CustomerSummary,
} from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader } from '@/components/ui/Card';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { Input } from '@/components/ui/Input';
import { PageHeader } from '@/components/ui/PageHeader';
import { Pagination } from '@/components/ui/Pagination';
import { Select } from '@/components/ui/Select';
import { usePermissions } from '@/hooks/usePermissions';
import { useLocale } from '@/hooks/useLocale';
import { apiErrorMessage } from '@/services/apiClient';
import { formatDate } from '@/utils/format';
import { useCustomersQuery } from '../api/customers.api';
import { CUSTOMER_INTENT_EYE_EXAMINATIONS, CUSTOMER_INTENT_OPTICAL_ORDER } from '../intents';
import { CustomerStatusBadge } from '../components/CustomerStatusBadge';
import { QuickCreateCustomer } from '../components/QuickCreateCustomer';

const PAGE_SIZE = 20;

export function CustomersPage() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { can } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();
  const intent = (location.state as { intent?: string } | null)?.intent;
  const choosingForExaminations = intent === CUSTOMER_INTENT_EYE_EXAMINATIONS;
  const choosingForOpticalOrder = intent === CUSTOMER_INTENT_OPTICAL_ORDER;

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<CustomerStatusType | ''>('');
  const [page, setPage] = useState(1);
  const [isCreateOpen, setCreateOpen] = useState(false);

  const deferredSearch = useDeferredValue(search);

  const { data, isPending, isError, error, refetch, isFetching } = useCustomersQuery({
    page,
    pageSize: PAGE_SIZE,
    search: deferredSearch,
    status,
  });

  const canCreate = can(PERMISSIONS.CUSTOMERS_CREATE);
  const canEdit = can(PERMISSIONS.CUSTOMERS_EDIT);

  // Any filter change invalidates the current page number.
  const resetToFirstPage = () => setPage(1);

  const statusOptions = [
    { value: '', label: t('common.all') },
    ...Object.values(CustomerStatus).map((value) => ({
      value,
      label: t(`status.${value.toLowerCase()}`),
    })),
  ];

  const columns: Column<CustomerSummary>[] = [
    {
      key: 'customer',
      header: t('customers.fields.fullName'),
      primary: true,
      render: (customer) => (
        <span className="flex flex-col">
          <span className="text-ink font-medium">{customer.displayName}</span>
          <span className="text-muted font-mono text-xs" dir="ltr">
            {customer.customerNumber}
          </span>
        </span>
      ),
    },
    {
      key: 'mobile',
      header: t('customers.fields.mobile'),
      render: (customer) => (
        <span dir="ltr" className="text-muted">
          {customer.mobile}
        </span>
      ),
    },
    {
      key: 'email',
      header: t('customers.fields.email'),
      render: (customer) => (
        <span dir="ltr" className="text-muted">
          {customer.email ?? '—'}
        </span>
      ),
    },
    {
      key: 'language',
      header: t('customers.fields.preferredLanguage'),
      render: (customer) => (
        <span className="text-muted">
          {t(`customers.language.${customer.preferredLanguage.toLowerCase()}`)}
        </span>
      ),
    },
    {
      key: 'status',
      header: t('customers.fields.status'),
      render: (customer) => <CustomerStatusBadge status={customer.status} />,
    },
    {
      key: 'updatedAt',
      header: t('customers.fields.lastUpdated'),
      render: (customer) => (
        <span className="text-muted text-xs">{formatDate(customer.updatedAt, locale)}</span>
      ),
    },
    {
      key: 'actions',
      header: t('common.actions'),
      align: 'end',
      hideOnCard: true,
      render: (customer) => (
        <span className="inline-flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => void navigate(`/customers/${customer.id}`)}
            aria-label={t('customers.actions.viewNamed', { name: customer.displayName })}
          >
            <Eye className="size-4" aria-hidden />
            <span className="hidden lg:inline">{t('customers.actions.view')}</span>
          </Button>

          {canEdit ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => void navigate(`/customers/${customer.id}?edit=1`)}
              aria-label={t('customers.actions.editNamed', { name: customer.displayName })}
            >
              <Pencil className="size-4" aria-hidden />
              <span className="hidden lg:inline">{t('common.edit')}</span>
            </Button>
          ) : null}
        </span>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('customers.title')}
        subtitle={t('customers.subtitle')}
        actions={
          canCreate ? (
            <Button onClick={() => setCreateOpen(true)}>
              <UserPlus className="size-4" aria-hidden />
              {t('customers.create')}
            </Button>
          ) : null
        }
      />

      {choosingForExaminations ? (
        <Alert tone="info">{t('medical.navigation.chooseCustomer')}</Alert>
      ) : null}
      {choosingForOpticalOrder ? (
        <Alert tone="info">{t('opticalOrders.entry.chooseCustomer')}</Alert>
      ) : null}

      <Card>
        <CardHeader
          title={t('customers.title')}
          subtitle={data ? t('customers.count', { count: data.pagination.totalItems }) : undefined}
          action={
            <div className="flex flex-wrap items-center gap-2">
              <div className="w-44 sm:w-56">
                <Input
                  type="search"
                  value={search}
                  onChange={(event) => {
                    setSearch(event.target.value);
                    resetToFirstPage();
                  }}
                  placeholder={t('customers.search.placeholder')}
                  aria-label={t('customers.search.label')}
                />
              </div>

              <div className="w-32">
                <Select
                  options={statusOptions}
                  value={status}
                  aria-label={t('customers.fields.status')}
                  onChange={(event) => {
                    setStatus(event.target.value as CustomerStatusType | '');
                    resetToFirstPage();
                  }}
                />
              </div>
            </div>
          }
        />

        <DataTable
          caption={t('customers.tableCaption')}
          columns={columns}
          rows={data?.items}
          rowKey={(customer) => customer.id}
          isLoading={isPending}
          isError={isError}
          errorMessage={apiErrorMessage(error) ?? t('customers.loadError')}
          onRetry={() => void refetch()}
          emptyIcon={Users}
          emptyTitle={deferredSearch || status ? t('customers.noMatches') : t('customers.empty')}
          emptyDescription={
            deferredSearch || status ? t('customers.noMatchesHint') : t('customers.emptyHint')
          }
        />

        {data ? (
          <Pagination pagination={data.pagination} onPageChange={setPage} isBusy={isFetching} />
        ) : null}
      </Card>

      {canCreate ? (
        <QuickCreateCustomer
          isOpen={isCreateOpen}
          onClose={() => setCreateOpen(false)}
          onCreated={(customer) => {
            setCreateOpen(false);
            void navigate(`/customers/${customer.id}`);
          }}
          onUseExisting={(customerId) => void navigate(`/customers/${customerId}`)}
        />
      ) : null}
    </div>
  );
}
